<script setup>
import { ref, watch, onMounted, onBeforeUnmount, Teleport } from 'vue'

const props = defineProps({
  // Array of { id, name, rawDates, chartColor }
  dimensions: { type: Array, required: true },
  viewportStart: { type: Number, required: true },
  viewportEnd: { type: Number, required: true },
  compact: { type: Boolean, default: false },
})

const canvas = ref(null)
let animationId = null

// Stored draw state for tooltip lookups
let lastDrawState = null

// Tooltip state
const tooltipVisible = ref(false)
const tooltipX = ref(0)
const tooltipY = ref(0)
const tooltipDateRange = ref('')
const tooltipItems = ref([])
const tooltipTotal = ref(0)
let hoverX = -1

function parseDate(dateStr) {
  if (!dateStr) return null
  const hasTimezone = /Z|[+-]\d{2}:?\d{2}$/.test(dateStr)
  const d = new Date(hasTimezone ? dateStr : dateStr + 'Z')
  return isNaN(d.getTime()) ? null : d.getTime()
}

function generateBuckets(rawDates, vpStart, vpEnd, bucketCount, bucketDuration) {
  if (!isFinite(bucketCount) || bucketCount < 1 || bucketCount > 500) return []
  const buckets = new Array(bucketCount).fill(0)

  rawDates.forEach((item) => {
    if (item.startDate && item.endDate) {
      const s = parseDate(item.startDate)
      const e = parseDate(item.endDate)
      if (s === null || e === null) return
      for (let i = 0; i < bucketCount; i++) {
        const bStart = vpStart + i * bucketDuration
        const bEnd = bStart + bucketDuration
        if (s < bEnd && e > bStart) buckets[i]++
      }
    } else if (item.date) {
      const t = parseDate(item.date)
      if (t === null) return
      const count = item.count || 1
      if (t >= vpStart && t < vpEnd) {
        const idx = Math.min(Math.floor((t - vpStart) / bucketDuration), bucketCount - 1)
        buckets[idx] += count
      }
    }
  })

  return buckets
}

function parseRgba(color) {
  const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/)
  if (match) return { r: parseInt(match[1]), g: parseInt(match[2]), b: parseInt(match[3]) }
  return { r: 59, g: 130, b: 246 }
}

function formatShortDate(ts) {
  const d = new Date(ts)
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`
}

function draw() {
  const el = canvas.value
  if (!el) return

  const parent = el.parentElement
  if (!parent) return

  const width = parent.clientWidth
  const height = parent.clientHeight
  if (width === 0 || height === 0) return

  const dpr = window.devicePixelRatio || 1
  el.width = width * dpr
  el.height = height * dpr
  el.style.width = width + 'px'
  el.style.height = height + 'px'

  const ctx = el.getContext('2d')
  if (!ctx) return
  ctx.scale(dpr, dpr)
  ctx.clearRect(0, 0, width, height)

  const vpStart = props.viewportStart
  const vpEnd = props.viewportEnd
  if (vpEnd <= vpStart) return

  const dims = props.dimensions
  if (!dims || dims.length === 0) return

  const duration = vpEnd - vpStart
  const hourMs = 3600000
  const totalHours = duration / hourMs
  let bucketCount

  if (totalHours <= 48) bucketCount = Math.min(Math.ceil(totalHours), 48)
  else if (totalHours <= 24 * 7) bucketCount = Math.ceil(totalHours / 4)
  else if (totalHours <= 24 * 31) bucketCount = Math.ceil(totalHours / 24)
  else if (totalHours <= 24 * 180) bucketCount = Math.ceil(totalHours / (24 * 7))
  else bucketCount = Math.ceil(totalHours / (24 * 30))

  if (!isFinite(bucketCount) || bucketCount < 1) return
  bucketCount = Math.max(Math.min(Math.round(bucketCount), 500), 10)
  const bucketDuration = duration / bucketCount

  // Generate buckets per dimension and find global max
  const allBuckets = dims.map((dim) =>
    generateBuckets(dim.rawDates, vpStart, vpEnd, bucketCount, bucketDuration)
  )

  let globalMax = 0
  allBuckets.forEach((buckets) => {
    buckets.forEach((c) => { if (c > globalMax) globalMax = c })
  })
  if (globalMax === 0) return

  // Save draw state for tooltip lookups
  lastDrawState = { allBuckets, bucketCount, bucketDuration, vpStart, vpEnd, dims, width, height }

  const padding = props.compact ? 1 : 3
  const chartHeight = height - padding * 2

  // Draw crosshair line if hovering (detail mode only)
  if (!props.compact && hoverX >= 0 && hoverX <= width) {
    ctx.beginPath()
    ctx.moveTo(hoverX, 0)
    ctx.lineTo(hoverX, height)
    ctx.strokeStyle = 'rgba(107, 114, 128, 0.4)'
    ctx.lineWidth = 1
    ctx.setLineDash([3, 3])
    ctx.stroke()
    ctx.setLineDash([])
  }

  // Draw each dimension's line (back to front)
  dims.forEach((dim, dimIdx) => {
    const buckets = allBuckets[dimIdx]
    const { r, g, b } = parseRgba(dim.chartColor)

    const points = buckets.map((count, i) => {
      const x = bucketCount === 1 ? width / 2 : (i / (bucketCount - 1)) * width
      const y = padding + chartHeight * (1 - count / globalMax)
      return { x, y }
    })

    // Helper: draw path with slight smoothing (tension 0 = straight, 1 = full bezier)
    const tension = 0.25
    function drawSmoothedPath(pts) {
      ctx.moveTo(pts[0].x, pts[0].y)
      for (let i = 1; i < pts.length; i++) {
        const prev = pts[i - 1]
        const curr = pts[i]
        const dx = (curr.x - prev.x) * tension
        ctx.bezierCurveTo(prev.x + dx, prev.y, curr.x - dx, curr.y, curr.x, curr.y)
      }
    }

    // Filled area
    ctx.beginPath()
    ctx.moveTo(0, height)
    ctx.lineTo(points[0].x, points[0].y)
    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1]
      const curr = points[i]
      const dx = (curr.x - prev.x) * tension
      ctx.bezierCurveTo(prev.x + dx, prev.y, curr.x - dx, curr.y, curr.x, curr.y)
    }
    ctx.lineTo(width, points[points.length - 1].y)
    ctx.lineTo(width, height)
    ctx.closePath()
    ctx.fillStyle = `rgba(${r}, ${g}, ${b}, 0.12)`
    ctx.fill()

    // Line
    ctx.beginPath()
    drawSmoothedPath(points)
    ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, 0.8)`
    ctx.lineWidth = props.compact ? 1 : 1.5
    ctx.stroke()
  })
}

function scheduleRedraw() {
  if (animationId) cancelAnimationFrame(animationId)
  animationId = requestAnimationFrame(draw)
}

function onMouseMove(event) {
  if (props.compact || !lastDrawState) return

  const el = canvas.value
  if (!el) return

  const rect = el.getBoundingClientRect()
  const x = event.clientX - rect.left
  const y = event.clientY - rect.top
  const { allBuckets, bucketCount, bucketDuration, vpStart, dims, width } = lastDrawState

  // Determine bucket index from x position
  const fraction = x / width
  const bucketIdx = Math.max(0, Math.min(Math.floor(fraction * bucketCount), bucketCount - 1))

  // Bucket date range
  const bucketStart = vpStart + bucketIdx * bucketDuration
  const bucketEnd = bucketStart + bucketDuration
  tooltipDateRange.value = `${formatShortDate(bucketStart)} - ${formatShortDate(bucketEnd)}`

  // Per-dimension counts
  let total = 0
  const items = []
  dims.forEach((dim, i) => {
    const count = allBuckets[i][bucketIdx] || 0
    if (count > 0) {
      items.push({ name: dim.name, color: dim.chartColor, count })
      total += count
    }
  })

  tooltipItems.value = items
  tooltipTotal.value = total
  tooltipVisible.value = true

  // Position tooltip using fixed coordinates (teleported to body)
  tooltipX.value = event.clientX
  tooltipY.value = event.clientY

  // Update crosshair and redraw
  hoverX = x
  scheduleRedraw()
}

function onMouseLeave() {
  tooltipVisible.value = false
  hoverX = -1
  scheduleRedraw()
}

watch(
  () => [props.viewportStart, props.viewportEnd, props.dimensions],
  scheduleRedraw,
  { deep: true }
)

onMounted(scheduleRedraw)

onBeforeUnmount(() => {
  if (animationId) cancelAnimationFrame(animationId)
})
</script>

<template>
  <div style="position: absolute; inset: 0;">
    <canvas
      ref="canvas"
      :style="{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: compact ? 'none' : 'auto',
      }"
      @mousemove="onMouseMove"
      @mouseleave="onMouseLeave"
    />
    <Teleport to="body">
      <div
        v-if="tooltipVisible && tooltipItems.length > 0"
        class="timeline-tooltip"
        :style="{
          left: tooltipX + 12 + 'px',
          top: tooltipY - 12 + 'px',
          transform: 'translateY(-100%)',
        }"
      >
        <div class="timeline-tooltip-date">{{ tooltipDateRange }}</div>
        <div v-for="item in tooltipItems" :key="item.name" class="timeline-tooltip-row">
          <span class="timeline-tooltip-dot" :style="{ backgroundColor: item.color }" />
          <span class="timeline-tooltip-name">{{ item.name }}</span>
          <span class="timeline-tooltip-count">{{ item.count }}</span>
        </div>
        <div v-if="tooltipItems.length > 1" class="timeline-tooltip-total">
          Total: {{ tooltipTotal }}
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style>
.timeline-tooltip {
  position: fixed;
  z-index: 9999;
  pointer-events: none;
  background: hsl(var(--popover));
  color: hsl(var(--popover-foreground));
  border: 1px solid hsl(var(--border));
  border-radius: 6px;
  padding: 6px 8px;
  font-size: 11px;
  line-height: 1.4;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  white-space: nowrap;
  max-width: 250px;
}

.timeline-tooltip-date {
  font-weight: 600;
  margin-bottom: 3px;
  color: hsl(var(--muted-foreground));
}

.timeline-tooltip-row {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 1px 0;
}

.timeline-tooltip-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.timeline-tooltip-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
}

.timeline-tooltip-count {
  font-weight: 600;
  margin-left: 8px;
}

.timeline-tooltip-total {
  margin-top: 3px;
  padding-top: 3px;
  border-top: 1px solid hsl(var(--border));
  font-weight: 600;
  text-align: right;
}
</style>
